import Button from "@/common/components/Button";
import { FileUploader } from "@/common/components/FileUploader";
import MaskedTextField from "@/common/components/MaskedTextField";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useId, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ErrorMessage } from "@/common/components/ErrorMessage";
import { BankingDepositsData } from "@/features/privateCompanies/types/bankingDeposits/bankingDeposits.type";
import { useCreateBankingDepositFiscalDocument } from "@/features/privateCompanies/hooks/bankingDeposits/useCreateBankingDepositFiscalDocument";
import { UploadIcon } from "lucide-react";
import { bankingDepositFiscalDocumentSchema, BankingDepositFiscalDocumentValidationSchema } from "@/features/privateCompanies/schemas/bankingDeposits/fiscalDocument.schema";

interface CreateBankingDepositFiscalDocumentProps {
  bankingDeposit: BankingDepositsData;
}

const CreateBankingDepositFiscalDocument = ({ bankingDeposit }: CreateBankingDepositFiscalDocumentProps) => {
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [pdfFile, setPdfFile] = useState<File>();
  const [xmlFile, setXmlFile] = useState<File>();

  const {
    mutate: createBankingDepositFiscalDocument,
    isPending: isCreateBankingDepositFiscalDocumentPending,
  } = useCreateBankingDepositFiscalDocument({
    bankingDepositId: bankingDeposit.id,
    onSuccess() {
      resetForm();
      setIsOpen(false);
      setIsError(false);
      setMessages(['Nota fiscal adicionada com sucesso!']);

      queryClient.invalidateQueries({ queryKey: ['bankingDeposit', bankingDeposit.id] });
    },
    onError(error) {
      FormTransformers.errorsTransformer({ setError, setMessages })(error);
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<BankingDepositFiscalDocumentValidationSchema>({
    resolver: zodResolver(bankingDepositFiscalDocumentSchema),
    defaultValues: {
      number: '',
      serial: '',
    }
  });

  const onSubmit: SubmitHandler<BankingDepositFiscalDocumentValidationSchema> = (form) => createBankingDepositFiscalDocument({
    form,
    pdf: pdfFile,
    xml: xmlFile,
  });

  const resetForm = () => {
    reset();
    setPdfFile(undefined);
    setXmlFile(undefined);
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={(value) => {
          if (!value) {
            resetForm();
          }

          setIsOpen(value);
        }}
        Trigger={(
          <Button
            name="upload_fiscal_document"
            operationKlassToCheckPermission="Admin::Banking::Deposits::UploadFiscalDocument"
          >
            Upload manual

            <UploadIcon strokeWidth={1.5} size={20} />
          </Button>
        )}
        title="Adicionar NF"
        footer="Cadastrar NF"
        dataTestid="submit_upload_fiscal_document"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={isCreateBankingDepositFiscalDocumentPending}
        disabled={isCreateBankingDepositFiscalDocumentPending}
        body={(
          <form className="flex flex-col gap-4">
            <TextField
              label="Número da NF"
              maxLength={64}
              markAsRequired
              {...register('number')}
              errorMessage={errors.number?.message || undefined}
            />

            <MaskedTextField
              mask={''.padEnd(8, '0')}
              label="Número serial"
              markAsRequired
              {...register('serial')}
              errorMessage={errors.serial?.message || undefined}
            />

            <div className="flex flex-col gap-1">
              <FileUploader
                id={`fiscal-document-pdf-file-${useId()}`}
                title="Anexe o PDF da nota fiscal"
                className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                accept="application/pdf"
                fileName={pdfFile?.name}
                currentFileUrl={pdfFile ? URL.createObjectURL(pdfFile) : undefined}
                handleChange={setPdfFile}
                dataTestid="pdf_file_input"
              />

              {errors.pdf?.message && <ErrorMessage name='pdf' message={errors.pdf?.message} />}
            </div>

            <div className="flex flex-col gap-1">
              <FileUploader
                id={`fiscal-document-xml-file-${useId()}`}
                title="Anexe o XML da nota fiscal"
                className="w-full bg-yellow-op-2 text-white-400 border-none rounded-lg"
                accept="text/xml"
                fileName={xmlFile?.name}
                currentFileUrl={xmlFile ? URL.createObjectURL(xmlFile) : undefined}
                handleChange={setXmlFile}
                dataTestid="xml_file_input"
              />

              {errors.xml?.message && <ErrorMessage name='xml' message={errors.xml?.message} />}
            </div>
          </form>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isError}
        />
      )}
    </>
  );
};

export default CreateBankingDepositFiscalDocument;
